/// <amd-module name="Repos/Views/Settings/Repositories-Policies/Components/PolicyInputCheckboxTriggeredRadioGroup" />
import React = require("react");
import { IPolicy, SelectableItem } from "Repos/Views/Settings/Repositories-Policies/Contracts/Contracts";
import { IObservableValue } from "VSS/Core/Observable";
import { PolicyInputCheckbox } from "./PolicyInputCheckbox";
import { PolicyInputRadioGroup } from "./PolicyInputRadioGroup";

interface IPolicyInputCheckboxTriggeredRadioGroupProps {
    item: SelectableItem;
    isDisabled: boolean;
    policy: IPolicy;
    optionToSelectionMap: { [id: string]: IObservableValue<boolean> };
    onChange: (changes: { [itemId: string]: boolean }) => void;
}

export const PolicyInputCheckboxTriggeredRadioGroup: React.FunctionComponent<IPolicyInputCheckboxTriggeredRadioGroupProps> = ({
    item,
    policy,
    isDisabled,
    optionToSelectionMap,
    onChange
}) => {
    const { input } = policy;

    if (!input || !item || !optionToSelectionMap) {
        return <></>;
    }

    const [isChecked, setIsChecked] = React.useState<boolean>(false);
    const [radioGroupSelection, setRadioGroupSelection] = React.useState<string | undefined>(undefined);

    // Marks checkbox as checked if any of the underlying radio toggles are enabled.
    React.useEffect(() => {
        const enabledRadioGroupOption = item.subOptions?.find(i => optionToSelectionMap[i.id]?.value);
        setIsChecked(enabledRadioGroupOption !== undefined);
    }, [optionToSelectionMap]);

    React.useEffect(() => {
        const settings = policy?.settingValueObject;
        const inheritedSettings = policy?.inheritedSettingValueObject;
        const options = item?.subOptions;

        if (settings && options) {
            const enabledOptions = options.filter(o => settings[o.id] || (inheritedSettings && inheritedSettings[o.id]));
            if (enabledOptions.length === 1) {
                setRadioGroupSelection(enabledOptions[0].id);
            } else if (enabledOptions.length > 1) {
                // if any option includes more options, it's the primary one
                // For example: 'ResetAllVotes' includes 'ResetApproverVotes'
                const primaryOption = enabledOptions.find(o => o.includes !== undefined);
                setRadioGroupSelection(primaryOption?.id);
            }
        }
    }, [policy, item]);

    const onCheckboxChange = (isEnabled: boolean) => {
        setIsChecked(isEnabled);

        // Select first available option when checkbox switches to enabled.
        if (isEnabled && item && item.subOptions) {
            const firstRadioOptionId = item.subOptions[0].id;
            selectOption(firstRadioOptionId);
        } else {
            // Unselect all.
            selectOption(undefined);
            setRadioGroupSelection(undefined);
        }
    };

    // Selects radioButton with given ID and the option it includes (if applicable). Unselects the rest.
    const selectOption = (selectedId: string | undefined) => {
        const updatedSelections: { [key: string]: boolean } = {};
        const selectedOption = item.subOptions?.find(o => o.id === selectedId);

        item.subOptions?.forEach(o => {
            // select both the primary option and any other which might be included with the primary
            const isSelected = o.id === selectedOption?.id || o.id === selectedOption?.includes;
            updatedSelections[o.id] = isSelected;
        });

        onChange(updatedSelections);
    };

    return (
        <>
            <PolicyInputCheckbox item={item} isChecked={isChecked} isDisabled={isDisabled} onChange={onCheckboxChange} />
            <div className="padding-top-16 padding-left-16">
                {isChecked && item.subOptions && (
                    <PolicyInputRadioGroup
                        options={item.subOptions}
                        isDisabled={!!input.disabled || !!policy.disabled}
                        initialSelectedId={radioGroupSelection}
                        onSelect={selectOption}
                    />
                )}
            </div>
        </>
    );
};
